import { LucideIcon, Gift, Zap, Star, Crown } from "lucide-react";
import {
  Plan,
  PlanFeature,
  CurrentPlan,
  PlanId,
  ResetPeriod,
} from "@/types/plans";

export type PlanConfig = {
  icon: LucideIcon;
  color: string;
  badge: string | null;
};

export const PLAN_CONFIG: Record<string, PlanConfig> = {
  free: { icon: Gift, color: "text-muted-foreground", badge: null },
  starter: { icon: Zap, color: "text-blue-500", badge: null },
  growth: { icon: Star, color: "text-primary", badge: "Most Popular" },
  enterprise: { icon: Crown, color: "text-purple-500", badge: "Premium" },
};

export const PLAN_HIERARCHY: Record<PlanId, number> = {
  free: 0,
  starter: 1,
  growth: 2,
  enterprise: 3,
};

export const formatFeatureName = (name: string, isFree?: boolean): string => {
  if (isFree && name === "activated_creators") {
    return "Contact Creators";
  }

  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatFeatureValue = (
  feature: PlanFeature,
  upTo?: boolean
): string => {
  if (feature.type === "boolean") {
    return feature.enabled ? "Enabled" : "Disabled";
  }

  if (!feature.limit || feature.limit >= 50000) {
    return "Unlimited";
  }

  const periodMap: Record<ResetPeriod, string> = {
    billing_cycle: "month",
    forever: " organization",
    daily: "day",
    monthly: "month",
    none: "",
  };

  const period = periodMap[feature.reset_period] || "";

  let finalString = ` ${feature.limit.toLocaleString()}${
    period ? ` per ${period}` : ""
  }`;

  if (upTo) {
    finalString = `Up to ${finalString}`;
  }

  return finalString;
};

export const isCurrentPlan = (
  currentPlan: CurrentPlan | null,
  plan: Plan,
  isYearly: boolean
): boolean => {
  if (!currentPlan?.name) return false;
  const isSameName = currentPlan.name.toLowerCase() === plan.name.toLowerCase();

  if (plan.name.toLowerCase().includes("free") || currentPlan.isCustom)
    return isSameName;

  return (
    isSameName &&
    currentPlan.billing_period === (isYearly ? "yearly" : "monthly")
  );
};

export const isPlanSelectable = (
  currentPlan: CurrentPlan | null,
  targetPlan: Plan,
  isYearly: boolean
): boolean => {
  if (!currentPlan || currentPlan.name.toLowerCase() === "free") {
    return true;
  }

  const currentPlanName = currentPlan.name.toLowerCase();
  const targetPlanName = targetPlan.name.toLowerCase();
  const currentBilling = currentPlan.billing_period;

  if (isCurrentPlan(currentPlan, targetPlan, isYearly)) {
    return false;
  }

  if (currentBilling === "monthly") {
    if (targetPlanName === "free") return false;
    return true;
  }

  if (currentBilling === "yearly") {
    if (!isYearly) return false;

    const currentHierarchy = PLAN_HIERARCHY[currentPlanName as PlanId] || 0;
    const targetHierarchy = PLAN_HIERARCHY[targetPlanName as PlanId] || 0;

    if (currentPlanName === "growth") {
      return targetPlanName === "enterprise";
    }

    return targetHierarchy > currentHierarchy;
  }

  return true;
};
